import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Badge } from '@/common/ui/badge'
import { CheckCircle2, Trophy, TrendingUp, Zap, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface TournamentPresetOption {
	preset: string
	name: string
	description: string
	bannerImage: string
	minTurnover: number
	minDeposit: number
	minBetAmount: number
	maxTransactionDay: number
	prizes: Array<{
		rank: number
		prizeAmount: number
	}>
}

interface TournamentPresetSelectorProps {
	presets: TournamentPresetOption[]
	selectedPreset: string | null
	onSelectPreset: (preset: string) => void
	isLoading?: boolean
}

const PRESET_ICONS: Record<string, React.ReactNode> = {
	MINI: <Zap className="h-8 w-8 text-yellow-500" />,
	SMALL: <Trophy className="h-8 w-8 text-blue-500" />,
	MEDIUM: <TrendingUp className="h-8 w-8 text-purple-500" />,
	BIG: <Sparkles className="h-8 w-8 text-orange-500" />,
}

const PRESET_LABELS: Record<string, string> = {
	MINI: 'ขนาดเล็ก',
	SMALL: 'ขนาดกลาง',
	MEDIUM: 'ขนาดใหญ่',
	BIG: 'ขนาดใหญ่พิเศษ',
}

export default function TournamentPresetSelector({
	presets,
	selectedPreset,
	onSelectPreset,
	isLoading,
}: TournamentPresetSelectorProps) {
	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-2xl">เลือกรูปแบบทัวร์นาเมนต์</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
						{[1, 2, 3, 4].map((i) => (
							<div key={i} className="h-48 animate-pulse rounded-lg bg-muted" />
						))}
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-2xl">เลือกรูปแบบทัวร์นาเมนต์</CardTitle>
				<p className="text-base text-muted-foreground">
					เลือกรูปแบบที่เหมาะกับการใช้งานของคุณ
					ระบบจะตั้งค่าเริ่มต้นตามขนาดทัวร์นาเมนต์
				</p>
			</CardHeader>
			<CardContent>
				<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
					{presets.map((preset, index) => {
						const isSelected = selectedPreset === preset.preset
						const isRecommended = index === 1 // SMALL is recommended

						return (
							<button
								key={preset.preset}
								type="button"
								onClick={() => onSelectPreset(preset.preset)}
								className={cn(
									'group relative flex w-full flex-col items-center gap-4 rounded-lg border-2 p-6 transition-all hover:border-primary hover:shadow-lg',
									isSelected
										? 'border-primary bg-primary/5 shadow-md'
										: 'border-border bg-background',
								)}
							>
								<div className="absolute right-3 top-3 flex items-center gap-2">
									{isRecommended && (
										<Badge className="bg-green-600 text-xs hover:bg-green-700">
											แนะนำ
										</Badge>
									)}
									{isSelected && (
										<CheckCircle2 className="h-6 w-6 text-primary" />
									)}
								</div>

								<div className="flex flex-col items-center gap-3 pt-2">
									<div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
										{PRESET_ICONS[preset.preset] || (
											<Trophy className="h-8 w-8" />
										)}
									</div>
									<div className="text-center">
										<h3 className="mb-1 text-lg font-semibold">
											{preset.name}
										</h3>
										<Badge variant="outline" className="text-xs">
											{PRESET_LABELS[preset.preset] || preset.preset}
										</Badge>
									</div>
								</div>

								<p className="text-center text-sm leading-relaxed text-muted-foreground">
									{preset.description}
								</p>
							</button>
						)
					})}

					{/* Custom/Advanced Card */}
					<button
						type="button"
						onClick={() => onSelectPreset('CUSTOM')}
						className={cn(
							'group relative flex w-full flex-col items-center gap-4 rounded-lg border-2 border-dashed p-6 transition-all hover:border-primary hover:shadow-lg',
							selectedPreset === 'CUSTOM'
								? 'border-primary bg-primary/5 shadow-md'
								: 'border-border bg-background',
						)}
					>
						{selectedPreset === 'CUSTOM' && (
							<div className="absolute right-3 top-3">
								<CheckCircle2 className="h-6 w-6 text-primary" />
							</div>
						)}

						<div className="flex flex-col items-center gap-3 pt-2">
							<div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted text-3xl">
								⚙️
							</div>
							<div className="text-center">
								<h3 className="mb-1 text-lg font-semibold">กำหนดเอง</h3>
								<Badge variant="outline" className="text-xs">
									ขั้นสูง
								</Badge>
							</div>
						</div>

						<p className="text-center text-sm leading-relaxed text-muted-foreground">
							สร้างทัวร์นาเมนต์แบบกำหนดเองทุกอย่างด้วยตัวเอง
							เหมาะสำหรับผู้ที่ต้องการควบคุมแบบละเอียด
						</p>
					</button>
				</div>
			</CardContent>
		</Card>
	)
}
